import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../lib/baseQueries";

interface InitiateUploadResponse {
  uploadId: string;
  key: string;
  content: {
    id: string;
    assetId: string;
    metaId: string | null;
    userId: string;
  };
}

interface UploadPartResponse {
  signedUrl: string;
  key: string;
  partNumber: number;
  uploadId: string;
}

interface CompleteUploadResponse {
  success: boolean;
  location?: string;
  key: string;
}

interface UploadPart {
  PartNumber: number;
  ETag: string;
}

export const uploadApi = createApi({
  reducerPath: "uploadApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    initiateMultipartUpload: builder.mutation<
      InitiateUploadResponse,
      {
        contentType: string;
        filename: string;
        extension: string;
        mimeType: string;
        size: number;
      }
    >({
      query: ({ contentType, filename, extension, mimeType, size }) => ({
        url: "/v1/assets/presigned-url/multipart",
        method: "POST",
        body: { contentType, filename, extension, mimeType, size },
      }),
    }),

    getUploadPartUrl: builder.mutation<
      UploadPartResponse,
      { key: string; uploadId: string; partNumber: number }
    >({
      query: ({ key, uploadId, partNumber }) => ({
        url: "/v1/assets/presigned-url/upload-part",
        method: "POST",
        body: { key, uploadId, partNumber },
      }),
    }),

    completeMultipartUpload: builder.mutation<
      CompleteUploadResponse,
      { key: string; uploadId: string; parts: UploadPart[] }
    >({
      query: ({ key, uploadId, parts }) => ({
        url: "/v1/assets/presigned-url/multipart/complete",
        method: "POST",
        body: { key, uploadId, parts },
      }),
    }),

    abortMultipartUpload: builder.mutation<
      { success: boolean },
      { key: string; uploadId: string }
    >({
      query: ({ key, uploadId }) => ({
        url: "/v1/assets/presigned-url/multipart/abort",
        method: "POST",
        body: { key, uploadId },
      }),
    }),
  }),
});

export const {
  useInitiateMultipartUploadMutation,
  useGetUploadPartUrlMutation,
  useCompleteMultipartUploadMutation,
  useAbortMultipartUploadMutation,
} = uploadApi;
